import React, { FC, useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import API from 'api/API';
import { GetSerialNumbersResponse, SerialNumber } from 'api/Responses/SetSerialNumberResponses';
import Theme from 'App.theme';
import Button from 'components/Button';
import Page from 'components/Page';
import { useLanguage } from 'context/AppContext';
import AddSerialNumberModal from './AddSerialNumberModal';
import * as Styles from './ManageSerialNumbers.styles';
import SerialNumberBelongsToDifferentSetModal from './SerialNumberBelongsToDifferentSetModal';
import SerialNumberListItem from './SerialNumberListItem';

type ManageSerialNumbersParams = {
  code: string;
};

const ManageSerialNumbers: FC = () => {
  const [language] = useLanguage();
  const [addSerialNumberModalOpen, setAddSerialNumberModalOpen] = useState(false);
  const { code = '' } = useParams<ManageSerialNumbersParams>();
  const navigate = useNavigate();
  const [refreshData, setRefreshData] = useState(false); // State variable to trigger data refresh

  const [ussResponse, setUssResponse] = useState<GetSerialNumbersResponse | null>(null);
  const [rows, setRows] = useState<SerialNumber[]>([]);
  const [setUUID, setSetUUID] = useState<string | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [localStateErrorMessage, setLocalStateErrorMessage] = useState<string>('');
  const [serialNumberLocalState, setSerialNumberLocalState] = useState<string>('');

  useEffect(() => {
    void getData();
    // we want this to be loaded on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  const getData = async () => {
    try {
      const response = await API.UssEndpoint.getUniqueSetSerialNumbersForSet(code);
      setUssResponse(response);
      if (response) {
        setRows(response.uss_data); // Set the rows directly instead of concatenating
        setSetUUID(response.set_code);
      }
    } catch (e) {
      setUssResponse(null);
    }
  };

  const openSerialNumberBelongsToDifferentSetModal = async (errorMessage: string, serialNumber: string) => {
    await setLocalStateErrorMessage(errorMessage);
    await setSerialNumberLocalState(serialNumber);
    setIsErrorModalOpen(true);
  };

  const handleSerialNumberBelongsToDifferentSetModalClose = (shouldRefresh: boolean) => {
    setIsErrorModalOpen(false);
    if (shouldRefresh) {
      setRefreshData(!refreshData); // Toggle refreshData to trigger data refresh
    }
  };

  const handleAddSerialNumberClose = (shouldRefresh: boolean) => {
    setAddSerialNumberModalOpen(false);
    if (shouldRefresh) {
      setRefreshData(!refreshData); // Toggle refreshData to trigger data refresh
    }
  };

  const updateStateInParentComponent = (shouldRefesh: boolean) => {
    if (shouldRefesh) {
      setRefreshData(!refreshData); // Toggle refreshData to trigger data refresh
    }
  };

  return (
    <Page>
      <Styles.SetHeader>
        <h1>{code}</h1>
        <div style={{ textAlign: 'right' }}>
          <Button style={{ marginRight: '10px' }} onClick={() => setAddSerialNumberModalOpen(true)}>
            {language.ManageSerialNumbers.addSerialNumber}
          </Button>
          <Button onClick={() => navigate(-1)}>{language.ManageSerialNumbers.back}</Button>
        </div>
      </Styles.SetHeader>
      <Table sx={{ maxHeight: 'auto', marginTop: '50px' }}>
        <TableHead>
          <TableRow>
            <Styles.HeadTableCell align="center">{language.ManageSerialNumbers.serialNumber}</Styles.HeadTableCell>
            <Styles.HeadTableCell align="center">{language.ManageSerialNumbers.location}</Styles.HeadTableCell>
            <Styles.HeadTableCell align="center">{language.ManageSerialNumbers.active}</Styles.HeadTableCell>
            <Styles.HeadTableCell align="center">{language.ManageSerialNumbers.action}</Styles.HeadTableCell>
          </TableRow>
        </TableHead>
        {ussResponse?.uss_data.length === 0 ? (
          <TableRow>
            <TableCell align="center" colSpan={5} style={{ backgroundColor: Theme.greys.GREY0, height: '300px' }}>
              {language.ManageSerialNumbers.noResults}
            </TableCell>
          </TableRow>
        ) : (
          <TableBody>
            {rows.map((serialNumber, index) => {
              return (
                <SerialNumberListItem
                  key={index}
                  serialNumber={serialNumber}
                  setUUID={setUUID || ''}
                  setCode={code}
                  updateStateInParentComponent={updateStateInParentComponent}
                />
              );
            })}
          </TableBody>
        )}
      </Table>
      <AddSerialNumberModal
        open={addSerialNumberModalOpen}
        close={() => handleAddSerialNumberClose(true)}
        setUUID={setUUID}
        setSerialNumberBelongsToDifferentSetModalOpen={openSerialNumberBelongsToDifferentSetModal}
      />
      <SerialNumberBelongsToDifferentSetModal
        open={isErrorModalOpen}
        close={() => handleSerialNumberBelongsToDifferentSetModalClose(true)}
        setUUID={setUUID}
        errorMessage={localStateErrorMessage}
        setCode={code}
        serialNumber={serialNumberLocalState}
      />
    </Page>
  );
};

export default ManageSerialNumbers;
